import _ from 'lodash'
import { uuid } from 'vue-uuid'
import { store, entityQuery } from '@/main'
import { ftsByDataType, sorterByDataType } from 'CORE/components/data-type/comparators'
import { currentTimestamp } from 'CORE/utils/date'
import { active, inactive } from 'ENUMS/entity-states-groups'

/**
 * @var {ApiMiddleware}
 */
let api = {}

/**
 * Set root path for api
 * @param {ApiMiddleware} middlewareObject
 */
export const setTasksApiMiddleware = middlewareObject => {
    api = middlewareObject
}

export default () => ({
    state: {
        tasks: [],
    },
    getters: {
        tasksBy: state => ({ projectId, taskStatus }) => {
            let tasks = state.tasks
            if (projectId !== undefined) {
                tasks = _.filter(tasks, ['project.id', projectId])
            }
            if (taskStatus !== undefined) {
                tasks = _.filter(tasks, t => {
                    try {
                        const entityStateProperty = entityQuery.onePropertyByTag(t, 'entityState')
                        const entityStateId = entityStateProperty.singleValue
                        const state = _.find(_.get(store.getters.currentUser, `company.settings.entity-states.states`, []), ['id', entityStateId])
                        if (taskStatus === '$active') {
                            return _.map(active, s => s.id).includes(state.group) && _.get(t, 'validUntil', null) === null
                        }
                        if (taskStatus === '$inactive') {
                            return _.map(inactive, s => s.id).includes(state.group) || _.get(t, 'validUntil', null) !== null
                        }
                        
                        if (taskStatus === '$all' && _.get(t, 'validUntil', null) === null) return true
                        if (taskStatus === '$deleted' && _.get(t, 'validUntil', null) !== null) return true
                        if (_.get(t, 'validUntil', null) !== null) return false
                        return state.group === taskStatus
                    } catch (e) {
                        const isDone = _.get(_.head(entityQuery.propertyValuesByTag(t, 'done')), 'value.value', false)
                        if (taskStatus === 'active' && isDone === false) return true
                        if (taskStatus === 'done' && isDone === true) return true
                    }
                })
            }
            return tasks
        },
        tasks: state => _.filter(state.tasks, { validUntil: null }),
        disabledTasks: state => _.filter(state.tasks, t => {
            if (t.validUntil !== null) return true
        }),
        tasksByStateId: state => stateId => {
            return _.filter(state.tasks, t => {
                // console.info(stateId)
                if (_.get(t, 'validUntil', null) !== null) return false
                try {
                    const entityStateProperty = entityQuery.onePropertyByTag(t, 'entityState')
                    const entityStateId = entityStateProperty.singleValue
                    const state = _.find(_.get(store.getters.currentUser, `company.settings.entity-states.states`, []), ['id', entityStateId])
                    return state.group === stateId
                } catch (e) {
                    console.warn(e)
                    const isDone = _.get(_.head(entityQuery.propertyValuesByTag(t, 'done')), 'value.value', false)
                    if (stateId === 'active' && isDone === false) return true
                    if (stateId === 'done' && isDone === true) return true
                }
            })
        },
        tasksStates: () => [
            { id: 'active', name: 'Aktivní', icon: 'fa-circle', color: 'GreenYellow' },
            { id: 'done', name: 'Hotové', icon: 'fa-check-square', color: 'ForestGreen' },
        ],
        taskById: state => id => _.find(state.tasks, ['id', id]),
        tasksByProjectId: state => projectId => _.filter(state.tasks, ['project.id', projectId]),
        taskTypesByProjectId: state => projectId => _.uniq(_.map(_.filter(state.tasks, ['project.id', projectId]), 'taskType.id')),
    },
    mutations: {
        setTasks: (state, tasks) => {
            state.tasks = _
                .chain(tasks)
                // .filter(t => _.get(t, 'validUntil', null) === null)
                .map(p => {
                    p.propertyValues = _.mapValues(p.propertyValues, (v, k) => {
                        return {
                            value: v.value,
                            $hidden: _.get(v, '$hidden', false),
                            get $fts() {
                                const dataType = _.get(store.getters.propertyTypeByKey(k), 'type')
                                return ftsByDataType(v, dataType)
                            },
                            get $srt() {
                                const dataType = _.get(store.getters.propertyTypeByKey(k), 'type')
                                return sorterByDataType(v, dataType)
                            },
                        }
                    })
                    return p
                })
                .value()
            return state
        },
        addTask: (state, newTask) => {
            state.tasks.push(newTask)
        },
    },
    actions: {
        async tasksLoad({ commit }) {
            const tasks = await api.get('/task')
            commit('setTasks', tasks)
        },
        async updateTask({ commit }, data) {
            return api.put(`/task/${data.id}?mode=remove_missing_properties`, data)
                .then(r => {
                    return r
                })
        },
        async createTask({ commit }, data) {
            return api.post('/task', data)
                .then(r => {
                    commit('addTask', data)
                    return r
                })
        },
        async createTemplateTasks({ commit }, data) {
            const oldTasks = this.getters.tasksByProjectId(data.oldProjectId)
            const oldToNew = {}
            const results = []
            _.forEach(oldTasks, task => {
                const oldTaskId = task.id
                _.set(task, 'done', null)
                _.set(task, 'due', null)
                _.set(task, 'duration', null)
                _.set(task, 'id', uuid.v4())
                _.set(oldToNew, oldTaskId, task.id)
                _.set(task, 'project.id', data.templateId)
                _.each(_.filter(data.settings, s => !s.bool), s => {
                    _.unset(task, 'propertyValues.' + s.name)
                })

                results.push(api.post('/task', task))
            })
            return Promise.all(results)
        },
        async createProjectFromTemplateTasks({ commit }, data) {
            const oldTasks = this.getters.tasksByProjectId(data.templateId)
            const oldToNew = {}
            const results = []
            _.forEach(oldTasks, task => {
                const oldTaskId = task.id
                _.set(task, 'done', null)
                _.set(task, 'due', null)
                _.set(task, 'duration', null)
                _.set(task, 'id', uuid.v4())
                _.set(oldToNew, oldTaskId, task.id)
                _.set(task, 'project.id', data.projectId)

                results.push(api.post('/task', task))
            })
            return Promise.all(results)
        },
        async disableTask({ rootGetters }, id) {
            const taskToDisable = rootGetters.taskById(id)
            taskToDisable.validUntil = currentTimestamp()
            await api.put(`/task/${id}`, taskToDisable)
        },
        async disableProjectTasks({ rootGetters, dispatch }, projectId) {
            const tasks = rootGetters.tasksByProjectId(projectId)
            await Promise.all(_.map(tasks, t => {
                t.validUntil = Math.floor(Date.now() / 1000)
                return api.put(`/task/${t.id}`, t)
            }))
            await dispatch('tasksLoad')
        },
    },
})
